import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { CreditCard, CheckCircle, Clock, XCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    revenue: 0
  });

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
      
      const confirmed = response.data.filter(p => p.status === 'RECEIVED' || p.status === 'CONFIRMED').length;
      const pending = response.data.filter(p => p.status === 'PENDING').length;
      const revenue = response.data
        .filter(p => p.status === 'RECEIVED' || p.status === 'CONFIRMED')
        .reduce((sum, p) => sum + p.valor, 0);
      
      setStats({
        total: response.data.length,
        confirmed,
        pending,
        revenue
      });
    } catch (error) {
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'RECEIVED':
      case 'CONFIRMED':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'RECEIVED': 'Confirmado',
      'CONFIRMED': 'Confirmado',
      'PENDING': 'Pendente',
      'OVERDUE': 'Vencido'
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gerenciar Pagamentos
            </h1>
            <p className="text-muted-foreground text-lg">
              Visualize todas as transações da plataforma
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="p-6 rounded-2xl bg-white border border-border">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="h-8 w-8 text-blue-500" />
                <span className="text-sm text-muted-foreground">Total</span>
              </div>
              <p className="text-3xl font-bold">{stats.total}</p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-border">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-sm text-muted-foreground">Confirmados</span>
              </div>
              <p className="text-3xl font-bold">{stats.confirmed}</p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-border">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="h-8 w-8 text-orange-500" />
                <span className="text-sm text-muted-foreground">Pendentes</span>
              </div>
              <p className="text-3xl font-bold">{stats.pending}</p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-border">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-8 w-8 text-primary" />
                <span className="text-sm text-muted-foreground">Receita</span>
              </div>
              <p className="text-3xl font-bold">R$ {stats.revenue.toFixed(2)}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Data</th>
                    <th className="text-left p-4 font-semibold">Usuário</th>
                    <th className="text-left p-4 font-semibold">Tipo</th>
                    <th className="text-left p-4 font-semibold">Valor</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">ID Transação</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-muted-foreground">
                        Nenhum pagamento registrado
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm">
                          {new Date(payment.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="p-4 text-sm">{payment.user_id}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                            {payment.tipo}
                          </span>
                        </td>
                        <td className="p-4 font-semibold">R$ {payment.valor.toFixed(2)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(payment.status)}
                            <span className="text-sm">{getStatusText(payment.status)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground font-mono">
                          {payment.asaas_payment_id}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
}
